/**
 * Submissions: the department user's own, and the compliance queue with the
 * review (accept, or request changes), the held universe review, and the
 * destruction record once a decision is made.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ResultsView from './ResultsView';
import CoaSyncDialog from './CoaSyncDialog';
import { Notice, Modal, Table, Empty, StatusPill, OptionCard, btn, input, label as labelCls, filterLabel } from './ui';
import { getJson, postJson, downloadBlob, fmtDate, fmtNum, userEmail } from './api';

const API_ROOT = process.env.REACT_APP_API_URL || '';

const REASONS = ['Timeliness', 'Classification', 'Eligibility', 'Documentation', 'Data Quality', 'Other'];

export default function SubmissionsView({ user, compliance, onResubmit, notice }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [protocol, setProtocol] = useState('');
  const [open, setOpen] = useState(null);      // submission being reviewed
  const [held, setHeld] = useState(null);      // { id, result }
  const [heldBusy, setHeldBusy] = useState(false);
  const [attest, setAttest] = useState(null);
  // Sync to COA from here (operator order 2026-10-16): the dialog opens by
  // itself right after an Accept that counts toward monitoring, and from the
  // "Sync to COA" action on any approved row. syncState marks the rows that
  // already score a COA row (submission id → rows), one call per load.
  const [sync, setSync] = useState(null);     // { submission, afterApproval }
  const [syncState, setSyncState] = useState({});

  const loadSyncState = useCallback(async () => {
    if (!compliance) return;
    try {
      const r = await fetch(`${API_ROOT}/api/coa/scrubber-submissions/sync-state`, { credentials: 'include' });
      const d = await r.json().catch(() => null);
      if (r.ok && d && d.success) setSyncState(d.state || {});
    } catch (_) { /* the marks are a convenience; the list stands without them */ }
  }, [compliance]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const d = await getJson(compliance ? '/submissions' : '/submissions?me=true');
      const rows = (d.submissions || []).slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setList(rows);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
    loadSyncState();
  }, [compliance, loadSyncState]);
  useEffect(() => { load(); }, [load]);

  // After a decision: an accepted universe that counts toward monitoring is
  // offered to COA at once, in place.
  const afterDecision = (d) => {
    const s = d && d.submission;
    if (!(compliance && s && s.status === 'approved' && s.monitoring !== false)) return false;
    setSync({ submission: s, afterApproval: true });
    return true;
  };

  const rows = useMemo(() => list.filter((s) => (!status || s.status === status) && (!protocol || s.protocol === protocol)), [list, status, protocol]);
  const me = userEmail(user);

  async function openHeld(s) {
    setHeldBusy(true); setError('');
    try { setHeld({ id: s.id, submission: s, result: await getJson(`/submissions/${s.id}/held`) }); }
    catch (e) { setError(e.message); }
    finally { setHeldBusy(false); }
  }
  async function openAttestation(s) {
    setError('');
    try { setAttest(await getJson(`/submissions/${s.id}/destruction`)); } catch (e) { setError(e.message); }
  }

  if (held) {
    const s = held.submission;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" className={btn.secondarySmall} onClick={() => setHeld(null)}>Back to submissions</button>
          <StatusPill status={s.status} />
          {s.held && <StatusPill status={s.held.status} />}
        </div>
        <Notice tone="amber">This is the scrubbed universe the submitter sent, decrypted for your review only. It carries member level data. Your view is recorded. When you accept or request changes, the held file is destroyed and only the PHI free summary remains.</Notice>
        <ResultsView result={held.result} title={`Submission #${s.id} · ${s.protocol} ${s.tableLabel}`} meta={`${s.entityLabel || 'Internal'} · submitted by ${s.submittedBy} on ${fmtDate(s.timestamp)}${s.period?.startDate ? ` · period ${s.period.startDate} to ${s.period.endDate}` : ''}`}
          actions={(
            <>
              <button type="button" className={btn.secondarySmall} onClick={() => downloadBlob(`/submissions/${s.id}/held/export`, { filename: `${s.protocol}_submission_${s.id}.xlsx` }).catch((e) => setError(e.message))}>Export Excel</button>
              <button type="button" className={btn.primary} onClick={() => setOpen(s)}>Record the decision</button>
            </>
          )} />
        {error && <Notice tone="red">{error}</Notice>}
        <ReviewDialog open={!!open} submission={open} onClose={() => setOpen(null)} onDone={(d) => { setOpen(null); setHeld(null); load(); const syncing = afterDecision(d); if (d.destruction && !syncing) setAttest({ ...d.destruction, submission_id: s.id }); }} />
        <AttestationDialog attest={attest} onClose={() => setAttest(null)} />
      </div>
    );
  }

  const pendingCount = list.filter((s) => s.status === 'submitted' || s.status === 'resubmitted').length;

  return (
    <div className="space-y-4">
      {notice && <Notice tone="emerald">{notice}</Notice>}
      {error && <Notice tone="red">{error}</Notice>}
      <div className="flex flex-wrap items-center gap-3">
        <span className={filterLabel}>Filters</span>
        <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white min-w-[140px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option><option value="submitted">Pending review</option><option value="resubmitted">Resubmitted</option><option value="changes_requested">Changes requested</option><option value="approved">Approved</option>
        </select>
        <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white min-w-[140px]" value={protocol} onChange={(e) => setProtocol(e.target.value)}>
          <option value="">All protocols</option>{['ODAG', 'CDAG', 'FA', 'SNPCC', 'CPE'].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <button type="button" className={btn.link} onClick={load}>Refresh</button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="mr-auto">
            <h3 className="text-sm font-semibold text-slate-900">{compliance ? 'Compliance review' : 'My submissions'}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{compliance ? `${pendingCount} awaiting a decision · ${list.length} total. Accept one to score the oversight calendar, or send it back with what to fix.` : `${list.length} submitted. Where each stands, and what compliance asked for.`}</p>
          </div>
        </div>
        {loading ? <div className="py-12 text-center text-slate-500">Loading…</div> : rows.length === 0 ? (
          <div className="py-12 text-center text-slate-500">{list.length ? 'No submissions match these filters.' : compliance ? 'Nothing has been submitted for review yet.' : 'You have not submitted a universe yet. Validate a file and submit it as final.'}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  {['Submitted', 'Protocol and table', 'Entity', 'Period', 'Rows', 'Issues', 'Status', 'Held file', ''].map((h, i) => (
                    <th key={i} className={`py-3 px-3 text-xs font-semibold text-slate-600 whitespace-nowrap ${i === 4 || i === 5 ? 'text-right' : ''}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((s) => {
                  const mine = s.submittedBy === me;
                  const pending = s.status === 'submitted' || s.status === 'resubmitted';
                  return (
                    <tr key={s.id} className="align-top hover:bg-slate-50/60">
                      <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDate(s.timestamp)}<div className="text-slate-400">#{s.id}{s.version > 1 ? ` · v${s.version}` : ''}</div></td>
                      <td className="px-3 py-3"><div className="font-semibold text-slate-900">{s.protocol}</div><div className="text-xs text-slate-600">{s.tableLabel}</div>{compliance && <div className="text-[11px] text-slate-400">{s.submittedBy}</div>}</td>
                      <td className="px-3 py-3 text-slate-700">{s.entityLabel || 'Internal'}</td>
                      <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{s.period?.startDate ? `${s.period.startDate} to ${s.period.endDate}` : 'Not declared'}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{fmtNum(s.universeRowCount)}</td>
                      <td className={`px-3 py-3 text-right tabular-nums ${s.errorCount > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fmtNum(s.errorCount)}</td>
                      <td className="px-3 py-3">
                        <StatusPill status={s.status} />
                        {s.complianceNotes && s.status !== 'approved' && <div className="text-xs text-slate-700 mt-1 max-w-xs">{s.complianceNotes}</div>}
                        {s.status === 'approved' && s.reviewedBy && <div className="text-[11px] text-slate-500 mt-1">{s.reviewedBy} · {fmtDate(s.reviewedAt)}{s.monitoring === false ? ' · record only' : ''}</div>}
                        {compliance && s.status === 'approved' && (syncState[String(s.id)] || []).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(syncState[String(s.id)] || []).map((r) => (
                              <span key={r.activity_id} title={`${r.measure_label} · ${r.entity_name} · ${r.app === 'fdr' ? 'FDR COA' : 'Internal COA'}${r.last_synced_at ? ` · ${fmtDate(r.last_synced_at)}` : ''}`}
                                className="inline-block text-[10px] font-semibold rounded-md px-1.5 py-0.5 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                                Synced · {r.period_label} · {r.app === 'fdr' ? 'FDR COA' : 'COA'}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs">
                        {s.held ? (
                          <div className="space-y-1">
                            <StatusPill status={s.held.status} />
                            {s.held.status === 'held' && s.held.expires_at && <div className="text-slate-500">until {new Date(s.held.expires_at).toLocaleDateString()}</div>}
                            {s.held.status === 'destroyed' && <button type="button" className={btn.link} onClick={() => openAttestation(s)}>Destruction record</button>}
                          </div>
                        ) : <span className="text-slate-400">None</span>}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex flex-col items-end gap-1.5">
                          {compliance && s.held?.status === 'held' && <button type="button" className={btn.small} disabled={heldBusy} onClick={() => openHeld(s)}>Review the universe</button>}
                          {compliance && pending && <button type="button" className={btn.secondarySmall} onClick={() => setOpen(s)}>Record the decision</button>}
                          {compliance && s.status === 'approved' && s.monitoring !== false && (
                            <button type="button" className={(syncState[String(s.id)] || []).length ? btn.secondarySmall : btn.small} onClick={() => setSync({ submission: s, afterApproval: false })}
                              title="Score the matching Precision COA row from this submission without leaving the Scrubber">
                              {(syncState[String(s.id)] || []).length ? 'Sync to COA again' : 'Sync to COA'}
                            </button>
                          )}
                          {!compliance && mine && s.status !== 'approved' && <button type="button" className={btn.secondarySmall} onClick={() => onResubmit && onResubmit(s)}>Resubmit a corrected file</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ReviewDialog open={!!open} submission={open} onClose={() => setOpen(null)} onDone={(d) => { setOpen(null); load(); const syncing = afterDecision(d); if (d.destruction && !syncing) setAttest({ ...d.destruction, submission_id: open?.id }); }} />
      <AttestationDialog attest={attest} onClose={() => setAttest(null)} />
      <CoaSyncDialog open={!!sync} submission={sync?.submission} afterApproval={!!sync?.afterApproval} onClose={() => setSync(null)} onSynced={loadSyncState} />
    </div>
  );
}

function ReviewDialog({ open, submission: s, onClose, onDone }) {
  const [action, setAction] = useState('accept');
  const [counts, setCounts] = useState(true);
  const [reason, setReason] = useState('Other');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { if (open) { setAction('accept'); setCounts(true); setReason('Other'); setNotes(''); setErr(''); setBusy(false); } }, [open]);
  if (!open || !s) return null;
  async function go() {
    if (action === 'reject' && !notes.trim()) { setErr('Say what must change.'); return; }
    setBusy(true); setErr('');
    try {
      const d = await postJson(`/submissions/${s.id}/review`, { action, notes, reason: action === 'reject' ? reason : undefined, counts_toward_monitoring: action === 'accept' ? counts : undefined });
      onDone(d);
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title={`Decision on submission #${s.id}`}>
      <p className="text-sm text-slate-500 -mt-2 mb-4">{s.protocol} {s.tableLabel} · {s.entityLabel || 'Internal'} · {fmtNum(s.universeRowCount)} rows · {fmtNum(s.errorCount)} issues</p>
      <div className="space-y-3">
        <OptionCard checked={action === 'accept'} onChange={() => setAction('accept')} title="Accept" tone="emerald">
          <span className="block text-xs text-slate-600">The submission is final. Its timeliness and error totals can score the oversight calendar.</span>
          {action === 'accept' && <label className="flex items-center gap-2 text-xs text-slate-700 mt-2"><input type="checkbox" className="accent-orange-600" checked={counts} onChange={(e) => setCounts(e.target.checked)} /> Counts toward COA monitoring (uncheck for a record only submission such as audit preparation)</label>}
        </OptionCard>
        <OptionCard checked={action === 'reject'} onChange={() => setAction('reject')} title="Request changes" tone="rose">
          <span className="block text-xs text-slate-600">The submitter corrects the file and resubmits.</span>
          {action === 'reject' && (
            <div className="mt-2"><label className={labelCls}>Reason</label><select className={input} value={reason} onChange={(e) => setReason(e.target.value)}>{REASONS.map((r) => <option key={r}>{r}</option>)}</select></div>
          )}
        </OptionCard>
        <div><label className={labelCls}>Notes {action === 'reject' ? '(required)' : '(optional)'}</label><textarea className={input} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={action === 'reject' ? 'What must change before this universe is accepted' : 'Anything the record should carry'} /></div>
        {s.held?.status === 'held' && <Notice tone="amber">Recording this decision destroys the held file. The hash, counts and the PHI free summary are kept.</Notice>}
        {err && <Notice tone="red">{err}</Notice>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={btn.secondary} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className={action === 'accept' ? btn.primary : btn.danger} onClick={go} disabled={busy}>{busy ? 'Recording…' : action === 'accept' ? 'Accept' : 'Request changes'}</button>
        </div>
      </div>
    </Modal>
  );
}

function AttestationDialog({ attest, onClose }) {
  if (!attest) return null;
  const rows = [
    ['Submission', attest.submission_id], ['File', attest.file_name], ['SHA-256', attest.sha256], ['Size', attest.size_bytes != null ? `${fmtNum(attest.size_bytes)} bytes` : ''], ['Rows', fmtNum(attest.row_count)],
    ['Held since', fmtDate(attest.held_at)], ['Destroyed', fmtDate(attest.destroyed_at)], ['Destroyed by', attest.destroyed_by], ['Reason', attest.reason],
  ].filter(([, v]) => v !== undefined && v !== null && v !== '');
  return (
    <Modal open={!!attest} onClose={onClose} title="Destruction record">
      <p className="text-sm text-slate-500 -mt-2 mb-4">The held universe file was destroyed. What remains is this record, the PHI free error and timeliness summary, and the counts the oversight calendar uses.</p>
      <dl className="rounded-xl border border-slate-200 overflow-hidden text-sm">
        {rows.map(([k, v], i) => <div key={k} className={`flex gap-3 px-4 py-2.5 ${i % 2 ? 'bg-slate-50/60' : 'bg-white'}`}><dt className="w-32 text-xs font-semibold text-slate-500 pt-0.5">{k}</dt><dd className="flex-1 text-slate-800 break-all">{String(v)}</dd></div>)}
      </dl>
      <div className="flex justify-end mt-4"><button type="button" className={btn.secondary} onClick={onClose}>Close</button></div>
    </Modal>
  );
}
